/**
 * tripNightShare.ts — lets a campsite page drop that campsite into a night of
 * the trip being planned on the explorer ("Add to trip as tonight's stop").
 *
 * The explorer publishes a small summary of the trip in progress (its nights)
 * to localStorage; the campsite page reads it to offer "Night 2 · Halls Gap",
 * and writes back a pending add. The explorer applies that add when it next
 * loads or regains focus (or straight away via the `storage` event when it's
 * open in another tab). localStorage, not sessionStorage: campsite pages open
 * in a new tab, which gets a fresh session.
 */

export interface SharedNightOption {
  key: string;
  night: number;
  nights: number;
  date: string | null;
  name: string;
  suggested: boolean;
  stopId?: string;
  insertAfterStopId?: string;
  entityId?: number;
}

export interface SharedTripSummary {
  updatedAt: number;
  title: string;
  nights: SharedNightOption[];
}

export interface PendingNightAdd {
  createdAt: number;
  entity: { id: number; name: string; lat?: number; lng?: number };
  /** null = "add as an extra stop on the way". */
  night: SharedNightOption | null;
}

export const TRIP_SUMMARY_KEY = 'trip-planner.currentTripNights';
export const PENDING_ADD_KEY = 'trip-planner.pendingNightAdd';
/** A summary older than this is a trip the user has walked away from. */
const MAX_AGE_MS = 14 * 24 * 3600 * 1000;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function writeTripSummary(summary: SharedTripSummary | null): void {
  const ls = storage();
  if (!ls) return;
  try {
    if (!summary) ls.removeItem(TRIP_SUMMARY_KEY);
    else ls.setItem(TRIP_SUMMARY_KEY, JSON.stringify(summary));
  } catch {
    /* quota / private mode */
  }
}

export function readTripSummary(now = Date.now()): SharedTripSummary | null {
  const ls = storage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(TRIP_SUMMARY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SharedTripSummary;
    if (!parsed || !Array.isArray(parsed.nights)) return null;
    if (now - (parsed.updatedAt || 0) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writePendingAdd(add: PendingNightAdd): void {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(PENDING_ADD_KEY, JSON.stringify(add));
  } catch {
    /* ignore */
  }
}

/** Read and clear the pending add (so it's applied exactly once). */
export function takePendingAdd(now = Date.now()): PendingNightAdd | null {
  const ls = storage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(PENDING_ADD_KEY);
    if (!raw) return null;
    ls.removeItem(PENDING_ADD_KEY);
    const parsed = JSON.parse(raw) as PendingNightAdd;
    if (!parsed?.entity || typeof parsed.entity.id !== 'number') return null;
    if (now - (parsed.createdAt || 0) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
